import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { copyUrlToClipboard, getExternalHelpLink, invalidateSession, newFromClipboard } from '../core/state/core.actions';
import { selectOAuthProperties, selectUserData, selectUserPermissions, selectUserRole } from '../core/state/core.selectors';
import { LeftNavModel, RightNavModel } from './navigation';
import {
    selectDWSMCounter,
    selectLoadingDWSMCounter,
    selectLoadingWorkstockCounter,
    selectWorkstockCounter,
    selectWorkstockFavoriteCounterNavigation
} from './state/navigation.selectors';
import { NavigationUtils } from '../shared/utils/navigation-utils';
import { take, tap } from 'rxjs';
import { loadFavoriteCounter } from './state/navigation.actions';

@Component({
    selector: 'navigation',
    templateUrl: './navigation.component.html',
    styleUrls: ['./navigation.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class NavigationComponent implements OnInit {
    /* Be aware that all the menu components have OnPush detection strategy. Things work differently here. */
    isOpen: boolean = false;
    leftNavObject: LeftNavModel[] = [];
    angularRoutes: string[] = ['workstock', 'report', 'dwsm', 'analysis/new', 'analysis-search', 'archive-search'];
    favoriteCounter: number | undefined;

    $userData = this.store.select(selectUserData);
    $userPermissions = this.store.select(selectUserPermissions);
    $userRole = this.store.select(selectUserRole);
    $loadingWorkstockCounter = this.store.select(selectLoadingWorkstockCounter);
    $workstockCounter = this.store.select(selectWorkstockCounter);
    $loadingDWSMCounter = this.store.select(selectLoadingDWSMCounter);
    $dwsmCounter = this.store.select(selectDWSMCounter);
    $workstockFavoriteCounter = this.store.select(selectWorkstockFavoriteCounterNavigation).pipe(
        tap(favoriteCounter => {
            this.favoriteCounter = favoriteCounter;
        })
    );

    constructor(private router: Router, private store: Store) {}

    ngOnInit(): void {
        this.store.dispatch(loadFavoriteCounter());
    }

    goToUrl: goToUrl = (url: string, identifier?: string) => {
        if (this.angularRoutes.some(angularRoute => url.includes(angularRoute))) {
            // if the route exists in new angular use angular router to navigate
            if (url.includes('/dwsm/dashboard')) {
                localStorage.setItem('openDwsmPopup', 'true');
                if (identifier === 'fromXsens') {
                    localStorage.removeItem('dwsmParams');
                } else if (identifier === 'fromDwsmLite') {
                    localStorage.setItem('dwsmParams', JSON.stringify({ analysisKey: null, isTmo: false, isLite: true }));
                }
            } else if (url.includes('dwsm/dashboard')) {
                localStorage.removeItem('dwsmParams');
            }
            this.router.navigateByUrl(url);
        } else {
            if (identifier === 'fromClipboard') {
                this.store.dispatch(newFromClipboard());
                return;
            }
            NavigationUtils.openUrl(url); // else use window.href to navigate outside angular context to angularjs
        }
    };

    goToWorkstock = () => {
        const route = (this.favoriteCounter ?? 0) > 0 ? 'workstock/favorites' : 'workstock/analysis';
        this.router.navigate([route]);
    };

    toggleFoldMenu(): void {
        this.isOpen = !this.isOpen;
    }

    shareUrl = () => {
        this.store.dispatch(copyUrlToClipboard({ url: NavigationUtils.getCurrentUrl() }));
    };

    getObjFromNavMenu(navigationObj: LeftNavModel[]) {
        //use this function to get the nav obj from nav-menu component and pass it to fold-menu since they share same obj
        this.leftNavObject = navigationObj;
    }

    logout(): void {
        this.store.dispatch(invalidateSession());
        this.store
            .select(selectOAuthProperties)
            .pipe(take(1))
            .subscribe(oAuthProperties => {
                NavigationUtils.openUrl(
                    oAuthProperties!.url + '/' + oAuthProperties!.endSessionPath + '?id_token_hint=' + window.localStorage.getItem('authorizationData')
                );
                window.localStorage.removeItem('authorizationData');
                setTimeout(function () {
                    NavigationUtils.openUrl('/');
                }, 1000);
            });
    }

    rightNavigation: RightNavModel[] = [
        {
            name: 'DWSM.DASHBOARD.HEADER.LBL',
            icon: 'assets/images/dwsm_white.png',
            iconIsImage: true,
            alt: 'DWSM Dashboard',
            route: 'dwsm/dashboard',
            callFunc: this.goToUrl,
            permission: true
        },
        {
            name: 'PAGE.TOP_RIGHT_NAV.SHARE_BUTTON.TXT',
            icon: 'zmdi zmdi-share',
            route: '',
            callFunc: this.shareUrl,
            permission: true
        },
        {
            name: 'PAGE.TOP_RIGHT_NAV.HELP.TXT',
            icon: 'zmdi zmdi-pin-help',
            callFunc: () => this.store.dispatch(getExternalHelpLink({ key: 'GENERAL_MAIN' })),
            permission: true
        },
        {
            name: 'PAGE.TOP_RIGHT_NAV.NEWS.TXT',
            icon: 'zmdi zmdi-notifications',
            route: '/news/#/news/admin',
            callFunc: this.goToUrl,
            permission: true
        },
        {
            name: 'PAGE.GENERAL.WORK_BASKET.LBL',
            icon: 'zmdi zmdi-email-open',
            route: '',
            callFunc: this.goToWorkstock,
            permission: true
        },
        {
            name: '',
            icon: 'zmdi zmdi-account-circle',
            route: '/userprefs/#/userprefs',
            callFunc: this.goToUrl,
            permission: true
        }
    ];
}

export type goToUrl = (url: string, identifier?: string) => void;
